/**
 * Routes a visitor can reach before they have signed in (`src/app/index.tsx`).
 *
 * `/password_reset` is in `SKIP_AUTH_PATHS` alongside `/signup`; `/login` is
 * reached with no session at all.
 */
export const UNAUTHENTICATED_PATHS = [
  '/login',
  '/signup',
  '/password_reset',
] as const;

export const isUnauthenticatedPage = (pathname: string): boolean =>
  UNAUTHENTICATED_PATHS.some(
    (path) => pathname === path || pathname.startsWith(`${path}/`)
  );

/**
 * PostHog add-ons that each cost their own script fetch.
 *
 * Sentry, 14 days: `/signup` pulls six separate PostHog bundles before anyone
 * has signed in — recorder (p75 968ms), web-vitals (894ms), dead-clicks
 * (887ms), exception-autocapture (868ms), config (507ms) and surveys (437ms),
 * on roughly 490 of 680 page loads. There is no identified user to attribute
 * any of it to yet, and the same six ride along on `/login`.
 *
 * Only the add-ons are switched off: `init` still runs and still sends the
 * pageview, so acquisition funnels keep working. Anyone who signs in gets the
 * full set on the next load, because that load is no longer an auth route.
 *
 * `capture_performance` is deliberately left on. These are the first pages a
 * user lands on, so their web vitals are the ones worth watching — measuring
 * them is the point, not an overhead to trim. That keeps `web-vitals.js`
 * loading here (p75 894ms on `/signup`); the other four bundles still go.
 */
export const posthogOptionsForPath = (pathname: string) =>
  isUnauthenticatedPage(pathname)
    ? {
        // → posthog-recorder.js
        disable_session_recording: true,
        // → surveys.js
        disable_surveys: true,
        // → dead-clicks-autocapture.js
        capture_dead_clicks: false,
        // → exception-autocapture.js
        capture_exceptions: false,
        // → web-vitals.js. Kept on: the landing page's vitals are exactly the
        // ones we want to monitor.
        capture_performance: true,
      }
    : {};
